import type { EventDetail } from "gsintegrations";
import { axiosClient } from "../api/axiosClient";
import {
  claimEventIdempotencyKey,
  fingerprintEventCreatePayload,
  type EventIdempotencyAttempt,
} from "../utils/eventCreateIdempotency";
import type {
  BulkUpdateEventParticipationPayload,
  EventResponse,
  UpdateEventParticipationPayload,
} from "../types/event.types";
import { transformDateFields, type WithDate } from "../utils/dateTransformers";

type Insumo = {
  id: string;
  quantity: number;
  fromActivity?: boolean;
};

/** Member entry supporting both plain ID strings and objects with fromActivity flag */
export type MemberEntry = string | { id: string; fromActivity?: boolean };

/**
 * Transform event occurrence dates from ISO strings to local Date objects.
 * This prevents timezone issues where "2026-01-21" parsed as UTC
 * displays as the previous day in timezones behind UTC.
 *
 * @param event - Event detail from API
 * @returns Event with occurrence dates transformed to local Date objects
 */
export function transformEventDates(event: EventDetail): EventDetail {
  if (!event?.occurrences?.list?.length) {
    return event;
  }

  return {
    ...event,
    occurrences: {
      ...event.occurrences,
      list: transformDateFields(event.occurrences.list as WithDate[]),
    },
  };
}

/** Activity entry supporting both legacy string IDs and full objects with times */
export type ActivityEntry =
  | string
  | {
      id: string;
      startTime?: string;
      endTime?: string;
      checkinStartWindowMinutes?: number;
      checkinEndWindowMinutes?: number;
      qrReleaseOffsetMinutes?: number;
      qrCheckinGraceMinutes?: number;
    };

export type CreateEventPayload = {
  date: string; // ISO date string
  terreiroId: string;
  description: string;
  endTime: string;
  isAllDay: boolean;
  location: {
    city: string;
    complement?: string;
    isInTerreiro: boolean;
    knownLocation?: string;
    neighborhood: string;
    number: string;
    reference?: string;
    state: string;
    street: string;
    zipCode?: string;
  };
  name: string;
  members: MemberEntry[];
  insumos: Insumo[];
  startTime: string;
  type: string;
  activities: ActivityEntry[];
  minConfirmedMembers?: number;
  requiredActivityIds?: string[];
  isTemplate: boolean;
  sourceTemplateEventId?: string;
  qrCheckinEnabled?: boolean;
  checkinStartWindowMinutes?: number;
  checkinEndWindowMinutes?: number;
  qrReleaseOffsetMinutes?: number;
  qrCheckinGraceMinutes?: number;
  idempotencyKey?: string;
};

export type EditEventPayload = Partial<CreateEventPayload> & {
  id: string;
};

export type UpdateEventStatusPayload = {
  status: string;
};

let eventCreateAttempt: EventIdempotencyAttempt | null = null;

export const eventsRepository = {
  getUpcomingEvents: async (terreiroId?: string) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (!terreiroId) {
      return [];
    }

    const allEvents = [
      {
        id: 1,
        name: "Gira de Oxalá",
        type: "gira_aberta",
        date: new Date(),
        time: "19:00",
        participantsCount: 15,
        status: "confirmado" as const,
        terreiroId: "terreiro-1",
      },
      {
        id: 2,
        name: "Reunião Administrativa",
        type: "reuniao_administrativa",
        date: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
        time: "20:00",
        participantsCount: 8,
        status: "rascunho" as const,
        terreiroId: "terreiro-2",
      },
      {
        id: 3,
        name: "Festa de Iemanjá",
        type: "festa",
        date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Next week
        time: "18:00",
        participantsCount: 30,
        status: "confirmado" as const,
        terreiroId: "terreiro-1",
      },
    ];

    return allEvents.filter((event) => event.terreiroId === terreiroId);
  },
  createEvent: async (payload: CreateEventPayload) => {
    const { idempotencyKey: explicitKey, ...body } = payload;
    let idempotencyKey = explicitKey;
    if (!idempotencyKey) {
      const claimed = claimEventIdempotencyKey(
        eventCreateAttempt,
        fingerprintEventCreatePayload(body),
      );
      eventCreateAttempt = claimed.attempt;
      idempotencyKey = claimed.key;
    }
    const response = await axiosClient.post("/event/", body, {
      withCredentials: true,
      headers: { "Idempotency-Key": idempotencyKey },
    });
    if (eventCreateAttempt?.key === idempotencyKey) {
      eventCreateAttempt = null;
    }
    return response.data;
  },
  createEventTemplate: async (payload: CreateEventPayload) => {
    const response = await axiosClient.post("/event/template", payload, {
      withCredentials: true,
    });
    return response.data;
  },
  updateEvent: async (payload: EditEventPayload) => {
    const { id, ...updateData } = payload;
    const response = await axiosClient.put(`/event/${id}`, updateData, {
      withCredentials: true,
    });
    return response.data;
  },
  updateEventTemplate: async (payload: EditEventPayload) => {
    const { id, ...updateData } = payload;
    const response = await axiosClient.put(
      `/event/template/${id}`,
      updateData,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
  async getEventById(id: string, terreiroId?: string): Promise<EventDetail> {
    const params = terreiroId ? { terreiroId } : {};
    const response = await axiosClient.get<EventDetail>(`/event/${id}`, {
      params,
    });
    return transformEventDates(response.data);
  },
  async updateEventStatus(
    id: string,
    status: string,
    terreiroId?: string,
  ): Promise<void> {
    const params = terreiroId ? { terreiroId } : undefined;
    const response = await axiosClient.patch(
      `/event/${id}/status`,
      { status },
      { withCredentials: true, params },
    );
    return response.data;
  },

  /**
   * Update a member's participation status for an event.
   *
   * @param terreiroId - The terreiro ID
   * @param eventId - The event ID
   * @param memberId - The member ID
   * @param payload - The participation update payload
   * @returns The updated event with all members and participation status
   */
  async updateMemberParticipation(
    terreiroId: string,
    eventId: string,
    memberId: string,
    payload: UpdateEventParticipationPayload,
  ): Promise<EventResponse> {
    const { data } = await axiosClient.put<EventResponse>(
      `/terreiro/${terreiroId}/events/${eventId}/members/${memberId}/participation`,
      payload,
      { withCredentials: true },
    );
    return data;
  },

  /**
   * Bulk update participation for an event and its linked activities.
   *
   * @param terreiroId - The terreiro ID
   * @param eventId - The event ID
   * @param memberId - The member ID
   * @param payload - The bulk participation payload (event + activities)
   * @returns The updated event
   */
  async bulkUpdateParticipation(
    terreiroId: string,
    eventId: string,
    memberId: string,
    payload: BulkUpdateEventParticipationPayload,
  ): Promise<EventResponse> {
    const { data } = await axiosClient.post<EventResponse>(
      `/terreiro/${terreiroId}/events/${eventId}/members/${memberId}/participation/bulk`,
      payload,
      { withCredentials: true },
    );
    return data;
  },

  /**
   * Get an event template by ID.
   *
   * @param id - The event template ID
   * @param terreiroId - Optional terreiro ID for profile context
   * @returns The event template details
   */
  async getEventTemplateById(
    id: string,
    terreiroId?: string,
  ): Promise<EventDetail> {
    const params = terreiroId ? { terreiroId } : {};
    const response = await axiosClient.get<EventDetail>(
      `/event/template/${id}`,
      { withCredentials: true, params },
    );
    return transformEventDates(response.data);
  },
};
